import { useMemo, useState } from "react";
import {
  ChevronRight,
  RotateCcw,
  Bookmark,
  CheckCircle2,
  Sparkles,
  Wrench,
  BarChart3,
  ArrowLeft,
  AlertTriangle,
} from "lucide-react";
import { useBookmarks } from "../hooks/useBookmarks";
import { decisionTreeData, issuesData } from "../content";

type AnswerPathItem = {
  nodeId: string;
  question: string;
  answer: string;
};

type ScoreMap = Record<string, number>;

type DecisionOption = {
  label: string;
  next?: string;
  image?: string;
  score?: Record<string, number>;
};

type TreeNode = {
  id: string;
  type?: string;
  title?: string;
  question?: string;
  helperText?: string;
  confidence?: string;
  severity?: string;
  cause?: string;
  prevention?: string;
  doFirst?: string[];
  alternatives?: string[];
  fixes?: string[];
  avoid?: string[];
  supplies?: string[];
  options?: DecisionOption[];
};

type IssueItem = {
  id: string;
  title: string;
  category?: string;
  severity?: string;
  whatItLooksLike?: string;
  symptoms?: string[];
  causes?: string[];
  quickChecks?: string[];
  fixes?: string[];
  prevention?: string[];
  supplies?: string[];
  estimatedTime?: string;
  whenToCallTech?: string;
  whyThisDiagnosis?: string[];
  doFirst?: string[];
  confidenceNotes?: string[];
  proTips?: string[];
};

function buildNextScores(
  currentScores: ScoreMap,
  option?: DecisionOption
): ScoreMap {
  if (!option?.score) return currentScores;

  const updatedScores: ScoreMap = { ...currentScores };

  Object.entries(option.score).forEach(([key, value]) => {
    updatedScores[key] = (updatedScores[key] || 0) + Number(value);
  });

  return updatedScores;
}

function normalizeTopScores(scores: ScoreMap, limit = 3) {
  const sorted = Object.entries(scores)
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);

  const total = sorted.reduce((sum, [, value]) => sum + value, 0);

  return sorted.map(([key, value]) => ({
    key,
    raw: value,
    percent: total > 0 ? Math.round((value / total) * 100) : 0,
  }));
}

function getSeverityBadgeClass(severity?: string) {
  switch (severity) {
    case "critical":
      return "border-red-400/20 bg-red-500/10 text-red-300";
    case "high":
      return "border-orange-400/20 bg-orange-500/10 text-orange-300";
    case "medium":
      return "border-amber-400/20 bg-amber-500/10 text-amber-300";
    default:
      return "border-emerald-400/20 bg-emerald-500/10 text-emerald-300";
  }
}

function getConfidenceBadgeClass(confidence?: string) {
  switch (confidence) {
    case "high":
      return "border-emerald-400/20 bg-emerald-500/10 text-emerald-300";
    case "medium":
      return "border-cyan-400/20 bg-cyan-400/10 text-cyan-300";
    default:
      return "border-fuchsia-400/20 bg-fuchsia-500/10 text-fuchsia-200";
  }
}

function SectionPill({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
      <Icon className="h-4 w-4" />
      {label}
    </div>
  );
}

function InfoBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[22px] border border-white/10 bg-white/[0.04] p-5">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      <div className="mt-3 text-sm leading-6 text-white/72">{children}</div>
    </div>
  );
}

export function Diagnosis() {
  const { toggleBookmark, isBookmarked } = useBookmarks();

  const [currentNodeId, setCurrentNodeId] = useState("start_mode");
  const [history, setHistory] = useState<string[]>([]);
  const [answers, setAnswers] = useState<AnswerPathItem[]>([]);
  const [scores, setScores] = useState<ScoreMap>({});
  const [scoreHistory, setScoreHistory] = useState<ScoreMap[]>([]);
  const [finished, setFinished] = useState(false);

  const currentNode: TreeNode | undefined = decisionTreeData.tree[currentNodeId];
  const topScores = normalizeTopScores(scores, 3);

  const rankedIssues = useMemo(() => {
    return Object.entries(scores)
      .filter(([, value]) => value > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([id, score]) => {
        const issue = issuesData.issues.find((item: IssueItem) => item.id === id);
        return issue ? { ...issue, score } : null;
      })
      .filter(Boolean) as (IssueItem & { score: number })[];
  }, [scores]);

  const topIssue = rankedIssues[0];
  const isLegacyResult = currentNode?.type === "result";
  const showScoreResult = finished && !isLegacyResult;

  const progressPercent =
    finished || isLegacyResult
      ? 100
      : Math.max(10, Math.min(((history.length + 1) / 4) * 100, 100));

  const handleOptionClick = (option: DecisionOption) => {
    const nextScores = buildNextScores(scores, option);

    setHistory((prev) => [...prev, currentNodeId]);
    setScoreHistory((prev) => [...prev, scores]);
    setScores(nextScores);

    setAnswers((prev) => [
      ...prev,
      {
        nodeId: currentNodeId,
        question: currentNode?.question || "",
        answer: option.label,
      },
    ]);

    if (option.next && decisionTreeData.tree[option.next]) {
      setCurrentNodeId(option.next);
      return;
    }

    setFinished(true);
  };

  const handleBack = () => {
    if (history.length === 0) return;

    const newHistory = [...history];
    const previousId = newHistory.pop()!;

    const newAnswers = [...answers];
    newAnswers.pop();

    const newScoreHistory = [...scoreHistory];
    const previousScores = newScoreHistory.pop() || {};

    setHistory(newHistory);
    setAnswers(newAnswers);
    setScoreHistory(newScoreHistory);
    setScores(previousScores);
    setFinished(false);
    setCurrentNodeId(previousId);
  };

  const handleRestart = () => {
    setCurrentNodeId("start_mode");
    setHistory([]);
    setAnswers([]);
    setScores({});
    setScoreHistory([]);
    setFinished(false);
  };

  if (!finished && !currentNode) {
    return (
      <div className="min-h-screen bg-[#070b14] text-white">
        <div className="mx-auto max-w-4xl px-4 py-8 md:px-6 md:py-12">
          <div className="rounded-[26px] border border-white/10 bg-white/[0.04] p-8 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-red-400/15 bg-red-500/10">
              <AlertTriangle className="h-8 w-8 text-red-300" />
            </div>

            <h2 className="mt-5 text-2xl font-extrabold tracking-tight text-white">
              Something broke in the decision tree
            </h2>

            <p className="mt-3 text-sm leading-6 text-white/68 md:text-base">
              The app tried to open a diagnosis step that does not exist.
            </p>

            <button
              onClick={handleRestart}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/35 bg-cyan-400/20 px-6 py-3 text-sm font-semibold text-cyan-100 transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-400/30"
            >
              <RotateCcw className="h-4 w-4" />
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070b14] text-white">
      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-12">
        {/* HERO */}
        <section className="relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-6 md:p-10">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.16),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(217,70,239,0.12),transparent_30%)]" />

          <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/20 bg-fuchsia-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-fuchsia-200">
                <Wrench className="h-4 w-4" />
                Diagnosis Wizard
              </div>

              <h1 className="mt-5 text-4xl font-black tracking-tight md:text-5xl">
                Interactive Diagnosis
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-white/72 md:text-base">
                Answer guided questions to narrow down your DTF problem, surface
                the most likely cause, and get action steps faster.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/85">
                  <span className="font-bold text-white">
                    {finished || isLegacyResult ? "Complete" : `Step ${history.length + 1}`}
                  </span>
                </div>

                {answers.length > 0 ? (
                  <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-sm font-semibold text-cyan-300">
                    {answers.length} answers recorded
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  title: "Need a quick answer?",
                  description:
                    "Use the wizard path to narrow down likely causes step by step.",
                },
                {
                  title: "Need better confidence?",
                  description:
                    "The score engine surfaces top matches as your answers build.",
                },
                {
                  title: "Need to back up?",
                  description:
                    "Go back one step if you want to change a previous answer.",
                },
                {
                  title: "Need a fresh start?",
                  description:
                    "Restart anytime and run a new diagnosis from the beginning.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-[24px] border border-white/10 bg-white/[0.05] p-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-xl border border-emerald-300/20 bg-emerald-400/10 p-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-white">{item.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-white/70">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROGRESS */}
        <section className="mt-8 rounded-[26px] border border-white/10 bg-white/[0.04] p-5 md:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <SectionPill icon={BarChart3} label="Progress" />
              <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-white">
                {finished || isLegacyResult
                  ? "Diagnosis Complete"
                  : `Step ${history.length + 1}`}
              </h2>
              <p className="mt-2 text-sm leading-6 text-white/68">
                Category → Symptom → Check → Result
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {history.length > 0 ? (
                <button
                  onClick={handleBack}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white/85 transition hover:bg-white/10"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Go Back
                </button>
              ) : null}

              {(history.length > 0 || answers.length > 0 || finished) ? (
                <button
                  onClick={handleRestart}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/25 bg-cyan-400/10 px-5 py-3 text-sm font-semibold text-cyan-300 transition hover:border-cyan-300/40 hover:bg-cyan-400/15"
                >
                  <RotateCcw className="h-4 w-4" />
                  Start Over
                </button>
              ) : null}
            </div>
          </div>

          <div className="mt-6">
            <div className="mb-3 flex items-center justify-between text-sm text-white/65">
              <span>
                {finished || isLegacyResult ? "Complete" : `Progress ${Math.round(progressPercent)}%`}
              </span>
              <span>{answers.length} answers</span>
            </div>

            <div className="h-3 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-cyan-400 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <div className="mt-3 grid grid-cols-4 gap-2 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45">
              <div>Category</div>
              <div>Symptom</div>
              <div>Check</div>
              <div>Result</div>
            </div>
          </div>
        </section>

        {/* ANSWER PATH */}
        {answers.length > 0 ? (
          <section className="mt-6 rounded-[26px] border border-white/10 bg-white/[0.04] p-5 md:p-6">
            <SectionPill icon={Sparkles} label="Your Path" />

            <div className="mt-5 grid gap-3">
              {answers.map((item, index) => (
                <div
                  key={`${item.nodeId}-${index}`}
                  className="rounded-2xl border border-white/10 bg-black/15 p-4"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
                    Step {index + 1}
                  </p>
                  <p className="mt-2 text-sm text-white/60">{item.question}</p>
                  <p className="mt-2 text-sm font-semibold text-white">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* ACTIVE QUESTION */}
        {!isLegacyResult && !showScoreResult ? (
          <section className="mt-6 rounded-[28px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-6 md:p-8">
            <div className="max-w-4xl">
              <SectionPill icon={Wrench} label="Current Step" />

              <h2 className="mt-5 text-2xl font-extrabold tracking-tight text-white md:text-3xl">
                {currentNode?.question}
              </h2>

              {currentNode?.helperText ? (
                <p className="mt-3 text-sm leading-7 text-white/70 md:text-base">
                  {currentNode.helperText}
                </p>
              ) : null}

              <div className="mt-8 grid gap-3">
                {currentNode?.options?.map((option, index) => (
                  <button
                    key={`${option.label}-${index}`}
                    onClick={() => handleOptionClick(option)}
                    className="group flex w-full items-center justify-between rounded-[22px] border border-white/10 bg-black/15 p-5 text-left transition duration-300 hover:-translate-y-0.5 hover:border-cyan-300/25 hover:bg-white/[0.05]"
                  >
                    <div className="min-w-0">
                      <p className="text-base font-semibold text-white transition group-hover:text-cyan-200">
                        {option.label}
                      </p>
                    </div>

                    <ChevronRight className="h-5 w-5 shrink-0 text-white/45 transition group-hover:text-cyan-300" />
                  </button>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {/* LIVE SCORES */}
        {!finished && !isLegacyResult && topScores.length > 0 ? (
          <section className="mt-6 rounded-[26px] border border-white/10 bg-white/[0.04] p-5 md:p-6">
            <SectionPill icon={BarChart3} label="Live Analysis" />

            <h3 className="mt-4 text-xl font-bold text-white">Likely causes so far</h3>

            <div className="mt-5 grid gap-4">
              {topScores.map((item) => {
                const matchedIssue = rankedIssues.find((issue) => issue.id === item.key);

                return (
                  <div
                    key={item.key}
                    className="rounded-2xl border border-white/10 bg-black/15 p-4"
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-white">
                        {matchedIssue?.title || item.key}
                      </span>
                      <span className="text-sm font-bold text-cyan-300">
                        {item.percent}%
                      </span>
                    </div>

                    <div className="h-3 overflow-hidden rounded-full bg-white/10">
                      <div
                        className="h-full rounded-full bg-cyan-400 transition-all duration-300"
                        style={{ width: `${item.percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        {/* LEGACY RESULT */}
        {isLegacyResult && currentNode ? (
          <section className="mt-6 rounded-[28px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-6 md:p-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-emerald-300/20 bg-emerald-500/10">
                  <CheckCircle2 className="h-8 w-8 text-emerald-300" />
                </div>

                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.16em] text-cyan-300">
                    Diagnosis Complete
                  </p>
                  <h2 className="mt-2 text-3xl font-black tracking-tight text-white">
                    {currentNode.title}
                  </h2>
                </div>
              </div>

              <button
                onClick={() =>
                  toggleBookmark({
                    id: currentNodeId,
                    type: "result",
                    title: currentNode.title || "Diagnosis Result",
                  })
                }
                className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl border transition ${
                  isBookmarked(currentNodeId, "result")
                    ? "border-cyan-300/25 bg-cyan-400/10 text-cyan-300"
                    : "border-white/10 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Bookmark
                  className={`h-5 w-5 ${
                    isBookmarked(currentNodeId, "result") ? "fill-current" : ""
                  }`}
                />
              </button>
            </div>

            {topScores.length > 0 ? (
              <div className="mt-8 rounded-[24px] border border-cyan-300/15 bg-cyan-400/6 p-5">
                <h3 className="text-lg font-bold text-white">Probability Analysis</h3>

                <div className="mt-4 grid gap-4">
                  {topScores.map((item, index) => (
                    <div key={item.key}>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-white">
                          {index === 0 ? "Most Likely: " : ""}
                          {item.key}
                        </span>
                        <span className="text-sm font-bold text-cyan-300">
                          {item.percent}%
                        </span>
                      </div>

                      <div className="h-3 overflow-hidden rounded-full bg-white/10">
                        <div
                          className="h-full rounded-full bg-cyan-400 transition-all duration-300"
                          style={{ width: `${item.percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-6 flex flex-wrap gap-3">
              {currentNode.confidence ? (
                <span
                  className={`rounded-full border px-4 py-2 text-sm font-semibold ${getConfidenceBadgeClass(
                    currentNode.confidence
                  )}`}
                >
                  Confidence:{" "}
                  {currentNode.confidence.charAt(0).toUpperCase() +
                    currentNode.confidence.slice(1)}
                </span>
              ) : null}

              {currentNode.severity ? (
                <span
                  className={`rounded-full border px-4 py-2 text-sm font-semibold ${getSeverityBadgeClass(
                    currentNode.severity
                  )}`}
                >
                  Severity:{" "}
                  {currentNode.severity.charAt(0).toUpperCase() +
                    currentNode.severity.slice(1)}
                </span>
              ) : null}
            </div>

            <div className="mt-8 grid gap-5">
              {currentNode.doFirst && currentNode.doFirst.length > 0 ? (
                <InfoBlock title="Do This First">
                  <ol className="space-y-2">
                    {currentNode.doFirst.map((step, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-cyan-300/20 bg-cyan-400/10 text-xs font-bold text-cyan-300">
                          {index + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </InfoBlock>
              ) : null}

              {currentNode.cause ? (
                <InfoBlock title="Likely Cause">{currentNode.cause}</InfoBlock>
              ) : null}

              {currentNode.alternatives && currentNode.alternatives.length > 0 ? (
                <InfoBlock title="Other Possible Causes">
                  <ul className="space-y-2">
                    {currentNode.alternatives.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </InfoBlock>
              ) : null}

              {currentNode.fixes && currentNode.fixes.length > 0 ? (
                <InfoBlock title="Step-by-Step Fixes">
                  <ol className="space-y-2">
                    {currentNode.fixes.map((fix, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-cyan-300/20 bg-cyan-400/10 text-xs font-bold text-cyan-300">
                          {index + 1}
                        </span>
                        <span>{fix}</span>
                      </li>
                    ))}
                  </ol>
                </InfoBlock>
              ) : null}

              {currentNode.avoid && currentNode.avoid.length > 0 ? (
                <InfoBlock title="What Not To Do">
                  <ul className="space-y-2">
                    {currentNode.avoid.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </InfoBlock>
              ) : null}

              {currentNode.prevention ? (
                <InfoBlock title="Prevention">{currentNode.prevention}</InfoBlock>
              ) : null}

              {currentNode.supplies && currentNode.supplies.length > 0 ? (
                <InfoBlock title="Supplies Needed">
                  <div className="flex flex-wrap gap-2">
                    {currentNode.supplies.map((supply, index) => (
                      <span
                        key={index}
                        className="rounded-full border border-white/10 bg-black/15 px-3 py-1 text-xs text-white/70"
                      >
                        {supply}
                      </span>
                    ))}
                  </div>
                </InfoBlock>
              ) : null}
            </div>

            <div className="mt-8">
              <button
                onClick={handleRestart}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/35 bg-cyan-400/20 px-6 py-3 text-sm font-semibold text-cyan-100 transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-400/30"
              >
                <RotateCcw className="h-4 w-4" />
                Start New Diagnosis
              </button>
            </div>
          </section>
        ) : null}

        {/* SCORE RESULT */}
        {showScoreResult ? (
          <section className="mt-6 rounded-[28px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-6 md:p-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-emerald-300/20 bg-emerald-500/10">
                  <CheckCircle2 className="h-8 w-8 text-emerald-300" />
                </div>

                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.16em] text-cyan-300">
                    Diagnosis Complete
                  </p>
                  <h2 className="mt-2 text-3xl font-black tracking-tight text-white">
                    {topIssue?.title || "No clear diagnosis found"}
                  </h2>
                </div>
              </div>

              {topIssue ? (
                <button
                  onClick={() =>
                    toggleBookmark({
                      id: topIssue.id,
                      type: "result",
                      title: topIssue.title,
                    })
                  }
                  className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl border transition ${
                    isBookmarked(topIssue.id, "result")
                      ? "border-cyan-300/25 bg-cyan-400/10 text-cyan-300"
                      : "border-white/10 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Bookmark
                    className={`h-5 w-5 ${
                      isBookmarked(topIssue.id, "result") ? "fill-current" : ""
                    }`}
                  />
                </button>
              ) : null}
            </div>

            {topScores.length > 0 ? (
              <div className="mt-8 rounded-[24px] border border-cyan-300/15 bg-cyan-400/6 p-5">
                <h3 className="text-lg font-bold text-white">Top Matches</h3>

                <div className="mt-4 grid gap-4">
                  {topScores.map((item, index) => {
                    const matchedIssue = rankedIssues.find(
                      (issue) => issue.id === item.key
                    );

                    return (
                      <div key={item.key}>
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <span className="text-sm font-semibold text-white">
                            {index === 0 ? "Most Likely: " : ""}
                            {matchedIssue?.title || item.key}
                          </span>
                          <span className="text-sm font-bold text-cyan-300">
                            {item.percent}%
                          </span>
                        </div>

                        <div className="h-3 overflow-hidden rounded-full bg-white/10">
                          <div
                            className="h-full rounded-full bg-cyan-400 transition-all duration-300"
                            style={{ width: `${item.percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {topIssue ? (
              <div className="mt-8 grid gap-5">
                {topIssue.severity ? (
                  <div>
                    <span
                      className={`rounded-full border px-4 py-2 text-sm font-semibold ${getSeverityBadgeClass(
                        topIssue.severity
                      )}`}
                    >
                      Severity:{" "}
                      {topIssue.severity.charAt(0).toUpperCase() +
                        topIssue.severity.slice(1)}
                    </span>
                  </div>
                ) : null}

                {topIssue.whyThisDiagnosis && topIssue.whyThisDiagnosis.length > 0 ? (
                  <InfoBlock title="Why This Diagnosis">
                    <ul className="space-y-2">
                      {topIssue.whyThisDiagnosis.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </InfoBlock>
                ) : null}

                {topIssue.whatItLooksLike ? (
                  <InfoBlock title="What It Looks Like">
                    {topIssue.whatItLooksLike}
                  </InfoBlock>
                ) : null}

                {topIssue.symptoms && topIssue.symptoms.length > 0 ? (
                  <InfoBlock title="Symptoms">
                    <ul className="space-y-2">
                      {topIssue.symptoms.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </InfoBlock>
                ) : null}

                {topIssue.causes && topIssue.causes.length > 0 ? (
                  <InfoBlock title="Likely Causes">
                    <ul className="space-y-2">
                      {topIssue.causes.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </InfoBlock>
                ) : null}

                {topIssue.quickChecks && topIssue.quickChecks.length > 0 ? (
                  <InfoBlock title="Quick Checks">
                    <ul className="space-y-2">
                      {topIssue.quickChecks.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </InfoBlock>
                ) : null}

                {topIssue.doFirst && topIssue.doFirst.length > 0 ? (
                  <InfoBlock title="Do This First">
                    <ol className="space-y-2">
                      {topIssue.doFirst.map((step, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-cyan-300/20 bg-cyan-400/10 text-xs font-bold text-cyan-300">
                            {index + 1}
                          </span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </InfoBlock>
                ) : null}

                {topIssue.fixes && topIssue.fixes.length > 0 ? (
                  <InfoBlock title="Fixes">
                    <ol className="space-y-2">
                      {topIssue.fixes.map((fix, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-cyan-300/20 bg-cyan-400/10 text-xs font-bold text-cyan-300">
                            {index + 1}
                          </span>
                          <span>{fix}</span>
                        </li>
                      ))}
                    </ol>
                  </InfoBlock>
                ) : null}

                {topIssue.confidenceNotes && topIssue.confidenceNotes.length > 0 ? (
                  <InfoBlock title="Confidence Notes">
                    <ul className="space-y-2">
                      {topIssue.confidenceNotes.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </InfoBlock>
                ) : null}

                {topIssue.proTips && topIssue.proTips.length > 0 ? (
                  <InfoBlock title="Pro Tips">
                    <ul className="space-y-2">
                      {topIssue.proTips.map((tip, index) => (
                        <li key={index}>• {tip}</li>
                      ))}
                    </ul>
                  </InfoBlock>
                ) : null}

                {topIssue.prevention && topIssue.prevention.length > 0 ? (
                  <InfoBlock title="Prevention">
                    <ul className="space-y-2">
                      {topIssue.prevention.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </InfoBlock>
                ) : null}

                {topIssue.supplies && topIssue.supplies.length > 0 ? (
                  <InfoBlock title="Supplies Needed">
                    <div className="flex flex-wrap gap-2">
                      {topIssue.supplies.map((supply, index) => (
                        <span
                          key={index}
                          className="rounded-full border border-white/10 bg-black/15 px-3 py-1 text-xs text-white/70"
                        >
                          {supply}
                        </span>
                      ))}
                    </div>
                  </InfoBlock>
                ) : null}

                {topIssue.estimatedTime ? (
                  <InfoBlock title="Estimated Time">{topIssue.estimatedTime}</InfoBlock>
                ) : null}

                {topIssue.whenToCallTech ? (
                  <InfoBlock title="When To Call Tech">
                    {topIssue.whenToCallTech}
                  </InfoBlock>
                ) : null}
              </div>
            ) : (
              <div className="mt-8 rounded-[22px] border border-white/10 bg-white/[0.04] p-5 text-sm leading-6 text-white/68">
                No matching issue was found in your issue data for the collected
                scores.
              </div>
            )}

            <div className="mt-8">
              <button
                onClick={handleRestart}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/35 bg-cyan-400/20 px-6 py-3 text-sm font-semibold text-cyan-100 transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-400/30"
              >
                <RotateCcw className="h-4 w-4" />
                Start New Diagnosis
              </button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}